import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { useCamposDinamicos } from '../context/CamposDinamicosContext';

/**
 * Componente para renderizar campos dinámicos según el estado de una orden
 *
 * @param {Object} props
 * @param {number} props.idOrden - ID de la orden de servicio
 * @param {number} props.idEstado - ID del estado actual
 * @param {boolean} props.soloLectura - Si los campos son de solo lectura
 * @param {function} props.onValoresChange - Callback cuando cambian los valores
 */
const CamposDinamicosRenderer = ({
  idOrden,
  idEstado,
  soloLectura = false,
  onValoresChange
}) => {
  const {
    cargarCamposPorEstado,
    getCamposEstado,
    getValoresOrden,
    saveValorCampo,
    loading
  } = useCamposDinamicos();

  const [campos, setCampos] = useState([]);
  const [valores, setValores] = useState({});
  const [valoresOriginales, setValoresOriginales] = useState({});
  const [loadingValores, setLoadingValores] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Cargar campos del estado al montar o cambiar estado
   */
  useEffect(() => {
    if (idEstado) {
      const loadCampos = async () => {
        try {
          const camposEstado = await cargarCamposPorEstado(idEstado);
          setCampos(camposEstado || []);
        } catch (err) {
          console.error('Error cargando campos:', err);
          setError('Error al cargar campos del estado');
        }
      };
      loadCampos();
    }
  }, [idEstado, cargarCamposPorEstado]);

  /**
   * Cargar valores existentes si hay idOrden
   */
  useEffect(() => {
    if (idOrden) {
      const loadValores = async () => {
        try {
          setLoadingValores(true);
          const valoresOrden = await getValoresOrden(idOrden);

          // Convertir array de valores a objeto { id_campo: valor }
          const valoresObj = {};
          valoresOrden.forEach((v) => {
            const valor = v.valor_texto || v.valor_numerico || v.valor_fecha || v.valor_booleano || v.valor_json;
            valoresObj[v.id_campo] = valor;
          });

          setValores(valoresObj);
          setValoresOriginales(valoresObj);
        } catch (err) {
          console.error('Error cargando valores:', err);
        } finally {
          setLoadingValores(false);
        }
      };
      loadValores();
    }
  }, [idOrden, getValoresOrden]);

  /**
   * Manejar cambio de valor de campo
   */
  const handleValorChange = async (campo, nuevoValor) => {
    const nuevosValores = {
      ...valores,
      [campo.id_campo]: nuevoValor
    };

    setValores(nuevosValores);

    // Notificar al componente padre
    if (onValoresChange) {
      onValoresChange(nuevosValores);
    }

    // Si hay idOrden, guardar automáticamente
    if (idOrden) {
      try {
        // Preparar datos según tipo de campo
        const valorData = {
          id_orden_servicio: idOrden,
          id_campo: campo.id_campo
        };

        // Asignar valor según tipo de campo
        switch (campo.tipo_campo) {
          case 'texto':
          case 'textarea':
          case 'email':
          case 'telefono':
          case 'url':
            valorData.valor_texto = nuevoValor;
            break;
          case 'numero':
            valorData.valor_numerico = parseFloat(nuevoValor);
            break;
          case 'fecha':
            valorData.valor_fecha = nuevoValor;
            break;
          case 'boolean':
            valorData.valor_booleano = nuevoValor;
            break;
          case 'select':
          case 'archivo':
            valorData.valor_json = nuevoValor;
            break;
          default:
            valorData.valor_texto = nuevoValor;
        }

        await saveValorCampo(valorData);
      } catch (err) {
        console.error('Error guardando valor:', err);
      }
    }
  };

  /**
   * Renderizar campo según su tipo
   */
  const renderCampo = (campo) => {
    const valor = valores[campo.id_campo] ?? '';
    const esObligatorio = campo.es_obligatorio === true;

    const commonProps = {
      fullWidth: true,
      label: campo.etiqueta_display,
      required: esObligatorio,
      disabled: soloLectura,
      helperText: campo.descripcion || undefined
    };

    switch (campo.tipo_campo) {
      case 'texto':
      case 'email':
      case 'telefono':
      case 'url':
        return (
          <TextField
            {...commonProps}
            type={campo.tipo_campo === 'email' ? 'email' : campo.tipo_campo === 'url' ? 'url' : 'text'}
            value={valor}
            onChange={(e) => handleValorChange(campo, e.target.value)}
            placeholder={campo.configuracion_campo?.placeholder}
          />
        );

      case 'textarea':
        return (
          <TextField
            {...commonProps}
            multiline
            rows={4}
            value={valor}
            onChange={(e) => handleValorChange(campo, e.target.value)}
            placeholder={campo.configuracion_campo?.placeholder}
          />
        );

      case 'numero':
        const validaciones = campo.validaciones || {};
        return (
          <TextField
            {...commonProps}
            type="number"
            value={valor}
            onChange={(e) => handleValorChange(campo, e.target.value)}
            inputProps={{
              min: validaciones.min,
              max: validaciones.max,
              step: campo.configuracion_campo?.step || 1
            }}
          />
        );

      case 'fecha':
        return (
          <TextField
            {...commonProps}
            type="date"
            value={valor}
            onChange={(e) => handleValorChange(campo, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={valor === true || valor === 'true'}
                onChange={(e) => handleValorChange(campo, e.target.checked)}
                disabled={soloLectura}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  {campo.etiqueta_display}
                  {esObligatorio && <span style={{ color: 'red' }}> *</span>}
                </Typography>
                {campo.descripcion && (
                  <Typography variant="caption" color="textSecondary">
                    {campo.descripcion}
                  </Typography>
                )}
              </Box>
            }
          />
        );

      case 'select':
        const opciones = campo.configuracion_campo?.opciones || [];
        return (
          <FormControl {...commonProps}>
            <InputLabel>{campo.etiqueta_display}</InputLabel>
            <Select
              value={valor}
              onChange={(e) => handleValorChange(campo, e.target.value)}
              label={campo.etiqueta_display}
            >
              <MenuItem value="">
                <em>Seleccionar...</em>
              </MenuItem>
              {opciones.map((opcion, idx) => (
                <MenuItem key={idx} value={opcion}>
                  {opcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'archivo':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              {campo.etiqueta_display}
              {esObligatorio && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <TextField
              type="file"
              fullWidth
              disabled={soloLectura}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Por ahora solo guardamos el nombre del archivo
                  // En una implementación completa, subirías el archivo a un servidor
                  handleValorChange(campo, { nombre: file.name, size: file.size });
                }
              }}
            />
            {valor && valor.nombre && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Archivo: {valor.nombre}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <TextField
            {...commonProps}
            value={valor}
            onChange={(e) => handleValorChange(campo, e.target.value)}
          />
        );
    }
  };

  // Si está cargando
  if (loading || loadingValores) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  // Si hay error
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Si no hay campos para este estado
  if (campos.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6">
          Información Adicional del Estado
        </Typography>
        <Chip
          label={`${campos.filter(c => c.es_obligatorio).length} obligatorios`}
          size="small"
          color="primary"
        />
      </Box>

      <Grid container spacing={2}>
        {campos
          .sort((a, b) => (a.orden_display || 0) - (b.orden_display || 0))
          .map((campo) => (
            <Grid item xs={12} md={6} key={campo.id_campo}>
              {renderCampo(campo)}
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

export default CamposDinamicosRenderer;
